import { Barcode, CreditCard, QrCode } from 'lucide-react'
import { Card, EmptyState, MoneyValue } from '@shared/design-system/ui'
import type {
  PaymentMethodBreakdown as PaymentMethodBreakdownItem,
  PendingPix,
} from '../api/dashboard.contract'
import {
  barFill,
  barTrack,
  methodIcon,
  methodLabel,
  methodRow,
  methodTitle,
  pendingPixBox,
  pendingPixContent,
  pendingPixMeta,
  sectionHeader,
  sectionTitle,
} from './dashboard.css'

interface PaymentMethodBreakdownProps {
  methods: PaymentMethodBreakdownItem[]
  pendingPix: PendingPix
}

type PaymentMethod = PaymentMethodBreakdownItem['method']

const methodLabels: Record<PaymentMethod, string> = {
  CREDIT_CARD: 'Cartão',
  PIX: 'PIX',
  BOLETO: 'Boleto',
  PAYPAL: 'PayPal',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  AMAZON_PAY: 'Amazon Pay',
  UNKNOWN: 'Não informado',
}

function MethodIcon({ method }: { method: PaymentMethod }) {
  if (method === 'PIX') {
    return <QrCode size={16} />
  }
  if (method === 'BOLETO') {
    return <Barcode size={16} />
  }
  return <CreditCard size={16} />
}

function formatPct(value: number): string {
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
}

export function PaymentMethodBreakdown({ methods, pendingPix }: PaymentMethodBreakdownProps) {
  return (
    <Card padding="md">
      <div className={sectionHeader}>
        <span className={sectionTitle}>Meios de pagamento</span>
      </div>
      {methods.length === 0 ? (
        <EmptyState
          icon={<CreditCard size={22} />}
          title="Sem pagamentos no período"
          description="Quando houver pagamentos, a distribuição aparece aqui."
        />
      ) : (
        <div>
          {methods.map((item) => (
            <div key={item.method} className={methodRow}>
              <div className={methodTitle}>
                <span className={methodIcon}>
                  <MethodIcon method={item.method} />
                </span>
                <span className={methodLabel}>{methodLabels[item.method]}</span>
              </div>
              <MoneyValue amount={item.amount.amount} currency={item.amount.currency} size="sm" />
              <span className={pendingPixMeta}>{formatPct(item.pct)}</span>
              <div className={barTrack} aria-hidden="true">
                <div
                  className={barFill}
                  style={{ width: `${Math.min(Math.max(item.pct, 0), 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      <div className={pendingPixBox}>
        <div className={pendingPixContent}>
          <div className={methodLabel}>PIX pendente</div>
          <div className={pendingPixMeta}>
            {pendingPix.count}{' '}
            {pendingPix.count === 1
              ? 'pedido aguardando pagamento'
              : 'pedidos aguardando pagamento'}
          </div>
        </div>
        <MoneyValue
          amount={pendingPix.amount.amount}
          currency={pendingPix.amount.currency}
          size="sm"
        />
      </div>
    </Card>
  )
}
